import React from 'react';
import { createSelector } from '@reduxjs/toolkit';

import { RenderOrder } from '@paragon/three-shared';

import { useAppSelector } from '../../../components/hooks';
import { getSelectedItemIdentifiers } from '../../../library/selection/selectors';
import { getVisibleTrussEnvelopes } from '../../../store/selectors/layoutDataSelectors';
import { is3DLayoutCameraInUse } from '../../../store/selectors/layoutModeSelectors';
import { getActiveEnvelopeMode } from '../../../store/selectors/layoutVisualOptionsSelectors';
import { getTrussEnvelopeGuidsToHighlight } from '../../../store/selectors/trussGroups';
import { createGuidSet } from '../../../store/selectors/util/createGuidSet';
import { createSimpleSetSelector } from '../../../store/selectors/util/createSimpleSetSelector';
import { useAutoInvalidateOnChange } from '../../hooks/invalidation-hooks';
import { Segments } from '../Segments';
import { TrussEnvelopeStyle } from './colors';
import { RunInsertionVisuals } from './RunInsertionVisuals';
import { TrussEnvelopeCarriedConnectionVisualsController } from './TrussEnvelopeCarriedConnectionVisuals';
import { TrussEnvelopeInsertionVisual } from './TrussEnvelopeInsertionVisual';
import { TrussEnvelopeJackGirderConnectionVisualsController } from './TrussEnvelopeJackGirderConnectionVisuals';
import { TrussEnvelopeVisual } from './TrussEnvelopeVisual';

const getSelectedEnvelopeGuids = createSimpleSetSelector(getSelectedItemIdentifiers, (items) =>
  createGuidSet(items.filter((item) => item.type === 'trussEnvelope')),
);

export const getTrussEnvelopeStyles = createSelector(
  getVisibleTrussEnvelopes,
  getSelectedEnvelopeGuids,
  getTrussEnvelopeGuidsToHighlight,

  (envelopes, selectedEnvelopes, highlightedEnvelopes): Record<string, TrussEnvelopeStyle> =>
    Object.fromEntries(
      envelopes.map((envelope) => {
        const isHighlighted = highlightedEnvelopes.includes(envelope.guid);
        const isSelected = selectedEnvelopes.has(envelope.guid);
        const othersAreSelected = !isSelected && selectedEnvelopes.size > 0;

        const style = isHighlighted
          ? 'highlighted'
          : isSelected
            ? 'emphasised'
            : othersAreSelected
              ? 'faded'
              : 'normal';

        return [envelope.guid, style] as const;
      }),
    ),
);

export function TrussEnvelopesController() {
  const in3D = useAppSelector(is3DLayoutCameraInUse);
  const styles = useAppSelector(getTrussEnvelopeStyles);
  const renderMode = useAppSelector(getActiveEnvelopeMode);

  const trussEnvelopes = useAppSelector(getVisibleTrussEnvelopes);
  const selectedEnvelopeGuids = useAppSelector(getSelectedEnvelopeGuids);

  const inBulkRenamingMode = useAppSelector((state) => state.panelData.renameLayoutComponentsPanel.open === true);

  useAutoInvalidateOnChange(renderMode, trussEnvelopes);

  if (renderMode === 'none') return null;

  return (
    <Segments lineWidth={1.0} depthTest={in3D} depthWrite={in3D} renderOrder={RenderOrder.LayoutEnvelopeSegments}>
      {trussEnvelopes.map((envelope) => {
        const style = styles[envelope.guid] ?? 'normal';
        const isSelected = selectedEnvelopeGuids.has(envelope.guid);

        return (
          <TrussEnvelopeVisual
            key={envelope.guid}
            style={style}
            in3D={in3D}
            renderMode={renderMode}
            showEndLabels={isSelected && !inBulkRenamingMode}
            envelope={envelope}
          />
        );
      })}

      <TrussEnvelopeCarriedConnectionVisualsController envelopes={trussEnvelopes} />

      <TrussEnvelopeJackGirderConnectionVisualsController
        envelopes={trussEnvelopes}
        selectedEnvelopeGuids={selectedEnvelopeGuids}
      />

      <TrussEnvelopeInsertionVisual />

      <RunInsertionVisuals />
    </Segments>
  );
}
